import { useEffect, useRef, useState } from 'react'
import { isAncestor, Tree } from '@minoru/react-dnd-treeview'
import './playlist-editor.scss'
import PlaylistNode from '../../views/PlaylistView/PlaylistNode'
import PlaylistDragPreview from '../../views/PlaylistView/PlaylistDragPreview'
import PlaylistPlaceholder from '../../views/PlaylistView/PlaylistPlaceholder'
import Modal from 'react-modal/lib/components/Modal'
import PlaylistEditModal, {
  defaultEmbeddedSettings,
} from '../../views/PlaylistView/PlaylistEditModal/PlaylistEditModal'
import PlaylistGroupModal from '../../views/PlaylistView/PlaylistGroupModal'
import pastePlaylistData from '../../utils/pastePlaylistData'
import GeneralModal from '../GeneralModal/GeneralModal'
import TextInput from '../TextInput/TextInput'
import PlaylistEditorPlayerThemeModal from './PlaylistEditorPlayerThemeModal'
import { cloneDeep, set } from 'lodash'
import PlaylistEditorTrackThemeModal from './PlaylistEditorTrackThemeModal'
import LoadingBlocker from '../LoadingSpinner/LoadingBlocker'
import PlayerLinkModal from '../../views/PlaylistView/PlayerLinkModal/PlayerLinkModal'
import RenameModal from '../GeneralModal/RenameModal'

const PlaylistTree = ({
  values,
  onChange,
  onItemClick,
  onItemDoubleClick,
  onPublishChangesClick,
  openFolders,
  selectedItems,
  maxTreeHeight,
  hidePlaylistOptions,
  isLoading,
  isOutOfDate,
  copyBuffer,
}) => {
  const treeRef = useRef(null)
  const [groupToEdit, setGroupToEdit] = useState(null)
  const [playlistLinkValues, setPlaylistLinkValues] = useState(null)
  const [playlistToEdit, setPlaylistToEdit] = useState(null)
  const [playlistToTheme, setPlaylistToTheme] = useState(null)
  const [playlistToDelete, setPlaylistToDelete] = useState(null)
  const [trackToTheme, setTrackToTheme] = useState(null)
  const [trackToRename, setTrackToRename] = useState(null)
  const openFoldersRef = useRef(openFolders)
  const items = values?.data || []
  const hasItems = items.length > 0

  useEffect(() => {
    openFolders?.forEach((id) => {
      treeRef?.current?.open(id)
    })
  }, [openFolders])

  function handleGetLinkClick(node) {
    setPlaylistLinkValues({
      id: node.id,
    })
  }

  const handleDrop = (dropTreeData, options) => {
    let newTreeData = [...dropTreeData]
    const { monitor } = options
    let { destinationIndex, dropTargetId } = options
    const itemType = monitor.getItemType()
    if (itemType === 'track') {
      if (dropTargetId === 0) {
        dropTargetId = dropTreeData.findLast(
          (item) => item.id.slice(0, 1) === 'p',
        )?.id

        if (!dropTargetId) {
          return
        }
        destinationIndex = dropTreeData.length
      }
      const item = monitor.getItem()
      const tracks = item.tracks.reverse()
      let nextId = values.trackId
      tracks.forEach((track) => {
        nextId = nextId + 1
        const id = `t${nextId}`
        newTreeData.splice(destinationIndex, 0, {
          id,
          parent: dropTargetId,
          droppable: false,
          text: track.name,
          data: {
            trackId: track.id,
          },
        })
      })

      onChange({
        ...values,
        trackId: nextId,
        data: newTreeData,
      })
    } else {
      if (options.dropTarget) {
        const playlist = getPlaylistFromNode(options.dropTarget)
        let selectedId =
          items.find(
            (item) =>
              isAncestor(items, playlist.id, item.id) && item?.data?.selected,
          )?.id || 0

        if (selectedItems.length > 0) {
          selectedItems
            .filter(
              (item) =>
                item !== options.dragSourceId &&
                !selectedItems.find((search) =>
                  isAncestor(items, search, item),
                ),
            )
            .forEach((itemId) => {
              const index = newTreeData.findIndex((item) => {
                return item.id === itemId
              })
              const item = {
                ...newTreeData.splice(index, 1)[0],
                parent: options.dropTargetId,
              }

              newTreeData.splice(destinationIndex + 1, 0, item)
            })
        }

        newTreeData = newTreeData.map((item) => {
          if (
            item.id !== selectedId &&
            isAncestor(newTreeData, playlist.id, item.id)
          ) {
            if (item?.data?.selected) {
              if (!selectedId) {
                selectedId = item.id
                return item
              }
              return {
                ...item,
                data: {
                  ...item.data,
                  selected: false,
                },
              }
            }
          }
          return item
        })
      }

      onChange(
        {
          ...values,
          data: newTreeData,
        },
        { clearSelection: true },
      )
    }
    treeRef.current.open(dropTargetId)
  }

  function handleAddGroup(parentId) {
    setGroupToEdit({
      parent: parentId,
    })
  }

  function handleEditNodeClick(node) {
    const type = node.id.slice(0, 1)
    switch (type) {
      case 'p':
        return editPlaylist(node)
      case 'g':
        return setGroupToEdit(node)
      case 't':
        return
    }
  }

  function editPlaylist(node) {
    setPlaylistToEdit({ ...node })
  }
  function onSubmitEditPlaylist() {
    onChange({
      ...values,
      data: values.data.map((item) => {
        if (item.id === playlistToEdit.id) {
          return {
            ...item,
            ...playlistToEdit,
          }
        }
        return item
      }),
    })
    setPlaylistToEdit(null)
  }

  function handleDeleteClick(node) {
    if (node.id.slice(0, 1) === 'p') {
      setPlaylistToDelete(node)
    } else {
      const currentTree = values.data
      const newTree = currentTree.filter(
        (item) =>
          item.id !== node.id && !isAncestor(currentTree, node.id, item.id),
      )
      onChange({
        ...values,
        data: newTree,
      })
    }
  }

  function handleRemoveNodeClick() {
    const node = playlistToDelete
    const currentTree = values.data
    const newTree = currentTree.filter(
      (item) =>
        item.id !== node.id && !isAncestor(currentTree, node.id, item.id),
    )
    onChange({
      ...values,
      data: newTree,
    })
    setPlaylistToDelete(null)
  }

  function getPlaylistData(node) {
    const currentTree = values.data
    const playlistData = currentTree.filter(
      (item) =>
        item.id === node.id || isAncestor(currentTree, node.id, item.id),
    )
    return playlistData.length > 1 ? playlistData : null
  }

  function handlePublishChangesClick(node) {
    onPublishChangesClick(node)
  }

  function handleCanDrop(
    currentTree,
    { dragSourceId, dropTargetId, dragSource, dropTarget },
  ) {
    const type = dragSource?.type
    const dragType = type === 'track' ? 't' : dragSourceId.slice(0, 1)
    if (dropTargetId === 0) {
      if (dragType === 'p') {
        return true
      }
      return false
    }

    const ancestorCheck = selectedItems.reduce((acc, cur) => {
      return acc || isAncestor(currentTree, cur, dropTargetId)
    }, false)

    if (ancestorCheck) {
      return false
    }

    if (dropTargetId && dropTargetId !== dragSourceId) {
      const dropType = dropTargetId.slice(0, 1)
      if (dragType === 'g' && (dropType === 'p' || dropType === 'g')) {
        return true
      }
      if (dragType === 't' && (dropType === 'p' || dropType === 'g')) {
        return true
      }
    }

    return false
  }

  function getPlaylistFromNode(node) {
    return node.id.slice(0, 1) === 'p'
      ? node
      : values.data
          .filter((item) => item.id.slice(0, 1) === 'p')
          .find((item) => isAncestor(values.data, item.id, node.id))
  }

  function handleFavouriteClick(node) {
    const playlist = getPlaylistFromNode(node)

    onChange({
      ...values,
      data: values.data.map((item) => {
        if (isAncestor(values.data, playlist.id, item.id)) {
          if (item.id === node.id && !item.data.selected) {
            return {
              ...item,
              data: {
                ...node.data,
                selected: true,
              },
            }
          } else {
            return {
              ...item,
              data: {
                ...item.data,
                selected: false,
              },
            }
          }
        }
        return item
      }),
    })
  }

  function handlePlaylistGroupModalSubmit(group) {
    if (group.id) {
      onChange({
        ...values,
        data: values.data.map((item) => {
          if (item.id === group.id) {
            return {
              ...item,
              text: group.text,
              data: {
                ...item.data,
                open: group.data.open,
                art: group.data.art,
                description: group.data.description,
              },
            }
          }
          return item
        }),
      })
    } else {
      const nextId = values.groupId + 1
      const id = `g${nextId}`
      onChange({
        ...values,
        groupId: nextId,
        data: [
          ...values.data,
          {
            id,
            parent: group.parent,
            droppable: true,
            text: group.text,
            data: {
              art: group?.data?.art || '',
              open: group?.data?.open || false,
            },
          },
        ],
      })
      treeRef.current.open(group.parent)
    }
    setGroupToEdit(null)
  }

  function handleCanDrag(node) {
    return (
      node.id.slice(0, 1) === 'p' ||
      !!selectedItems.find((item) => node.id === item)
    )
  }

  function handlePasteClick(playlist) {
    const itemIndex = values.data.findLastIndex(
      (search) => search.parent === playlist,
    )
    onChange(pastePlaylistData(values, copyBuffer, playlist, itemIndex + 1))
  }

  function handleSetPlayerThemeClick(node) {
    setPlaylistToTheme(cloneDeep(node))
  }

  function handleSetTrackThemeClick(node) {
    const track = cloneDeep(node)
    const currentTheme = track?.data?.theme || { newTheme: true }
    set(track, 'data.theme', {
      bgColor: '#333',
      fgColor: '#5e263a',
      highlightColor: '#e44967',
      ...currentTheme,
    })
    setTrackToTheme(track)
  }

  function handleSetTrackNameClick(node) {
    setTrackToRename(node)
  }

  function handlePlaylistThemeModalSubmit(themeValues, flags) {
    onChange(
      {
        ...values,
        data: values.data.map((item) => {
          if (item.id === playlistToTheme.id) {
            return {
              ...item,
              data: {
                ...item.data,
                theme: themeValues,
              },
            }
          }
          return item
        }),
      },
      { ...flags, playlistId: playlistToTheme.id },
    )
  }

  function handlePlaylistTrackThemeModalSubmit(themeValues) {
    onChange({
      ...values,
      data: values.data.map((item) => {
        if (item.id === trackToTheme.id) {
          return {
            ...item,
            data: {
              ...item.data,
              theme: themeValues,
            },
          }
        }
        return item
      }),
    })
    setTrackToTheme(null)
  }

  function handleRenameTrack(name) {
    onChange({
      ...values,
      data: values.data.map((item) => {
        if (item.id === trackToRename.id) {
          return {
            ...item,
            data: {
              ...item.data,
              customName: name,
            },
          }
        }
        return item
      }),
    })
    setTrackToRename(null)
  }

  function handlePlaylistTrackThemeModalRemove() {
    const newData = cloneDeep(trackToTheme.data)
    delete newData.theme
    onChange({
      ...values,
      data: values.data.map((item) => {
        if (item.id === trackToTheme.id) {
          return {
            ...item,
            data: newData,
          }
        }
        return item
      }),
    })
    setTrackToTheme(null)
  }

  return (
    <div className="playlist-tree flex flex-col flex-1 px-6 overflow-auto">
      <div className="tree flex-1 overflow-auto flex flex-col">
        {hasItems && (
          <Tree
            tree={items}
            ref={treeRef}
            initialOpen={openFoldersRef.current}
            rootId={0}
            extraAcceptTypes={['track']}
            canDrag={handleCanDrag}
            canDrop={handleCanDrop}
            onDrop={handleDrop}
            render={(node, { depth, isOpen, onToggle, hasChild }) => (
              <PlaylistNode
                node={node}
                depth={depth}
                isOpen={isOpen}
                isLoading={isLoading}
                isOutOfDate={isOutOfDate}
                hasChild={hasChild}
                isSelected={selectedItems?.find((item) => node.id === item)}
                hidePlaylistOptions={hidePlaylistOptions}
                onItemClick={onItemClick}
                onItemDoubleClick={onItemDoubleClick}
                onGetLinkClick={handleGetLinkClick}
                onRemoveClick={handleDeleteClick}
                onEditClick={handleEditNodeClick}
                onFavouriteClick={handleFavouriteClick}
                onPasteClick={handlePasteClick}
                onToggle={onToggle}
                onAddGroupClick={handleAddGroup}
                onPublishChangesClick={handlePublishChangesClick}
                onSetPlayerThemeClick={handleSetPlayerThemeClick}
                onSetTrackThemeClick={handleSetTrackThemeClick}
                onSetTrackNameClick={handleSetTrackNameClick}
                copyBuffer={copyBuffer}
                isEditGroup={!!groupToEdit}
              />
            )}
            dragPreviewRender={(monitorProps) => (
              <PlaylistDragPreview monitorProps={monitorProps} />
            )}
            classes={{
              container: 'tree-container bg-gray ',
            }}
            sort={false}
            insertDroppableFirst={false}
            dropTargetOffset={5}
            placeholderRender={(node, { depth }) => (
              <PlaylistPlaceholder node={node} depth={depth} />
            )}
          />
        )}
      </div>
      {!!playlistToTheme?.id && (
        <Modal
          isOpen={!!playlistToTheme?.id}
          onRequestClose={() => setPlaylistToTheme(false)}
          contentLabel="Embedded Link Modal"
          className="modal w-[1115px] h-full animate-slide-out"
          overlayClassName="modal-overlay right"
        >
          <PlaylistEditModal
            values={playlistToTheme?.data?.theme}
            data={getPlaylistData(playlistToTheme)}
            id={playlistToTheme?.id}
            onCloseClick={() => setPlaylistToTheme(null)}
            onSubmit={handlePlaylistThemeModalSubmit}
            isLoading={isLoading}
          />
        </Modal>
      )}

      {!!groupToEdit && (
        <PlaylistGroupModal
          onSubmit={handlePlaylistGroupModalSubmit}
          onCancel={() => setGroupToEdit(null)}
          groupToEdit={groupToEdit}
        />
      )}

      {!!playlistLinkValues?.id && (
        <PlayerLinkModal
          id={playlistLinkValues?.id}
          onRequestClose={() => setPlaylistLinkValues(false)}
        />
      )}
      {!!playlistToEdit && (
        <GeneralModal
          isOpen={!!playlistToEdit}
          onRequestClose={() => setPlaylistToEdit(null)}
          title="Rename Playlist"
          buttons={[
            {
              className: 'button bg-pink',
              text: 'Save',
              disabled: !playlistToEdit?.text,
              onClick: () => {
                onSubmitEditPlaylist()
              },
            },
          ]}
        >
          <TextInput
            label="Enter Playlist Name"
            value={playlistToEdit.text}
            name="name"
            onEnter={onSubmitEditPlaylist}
            onChange={(evt) =>
              setPlaylistToEdit({ ...playlistToEdit, text: evt.target.value })
            }
          ></TextInput>
        </GeneralModal>
      )}

      {!!trackToTheme && (
        <Modal
          isOpen={!!trackToTheme?.id}
          onRequestClose={() => setTrackToTheme(false)}
          contentLabel="Override Track Theme"
          className="modal w-[800] h-full animate-slide-out"
          overlayClassName="modal-overlay right"
        >
          <PlaylistEditorTrackThemeModal
            initialValues={trackToTheme?.data?.theme}
            track={trackToTheme}
            onSubmit={handlePlaylistTrackThemeModalSubmit}
            onRemove={handlePlaylistTrackThemeModalRemove}
            onRequestClose={() => setTrackToTheme(false)}
          />
        </Modal>
      )}

      {!!playlistToDelete && (
        <GeneralModal
          isOpen={!!playlistToDelete}
          onRequestClose={() => {
            setPlaylistToDelete(null)
          }}
          title="Delete Playlist"
          buttons={[
            {
              className: 'button bg-red',
              text: 'Delete',
              onClick: handleRemoveNodeClick,
            },
          ]}
        >
          Are you sure you want to delete{' '}
          <span className="font-bold">{playlistToDelete?.text}</span>? This will
          also delete any web players linked to this playlist.
        </GeneralModal>
      )}

      {trackToRename ? (
        <RenameModal
          isOpen={!!trackToRename}
          onRequestClose={() => setTrackToRename(null)}
          title="Rename Track"
          label="Track Name"
          onSubmit={handleRenameTrack}
        />
      ) : null}
    </div>
  )
}

export default PlaylistTree
